import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import en from './en.json';
import de from './de.json';

export const LOCALES = ['de', 'en'] as const;
export type Locale = (typeof LOCALES)[number];

export type TranslateVars = Record<string, string | number | boolean | null | undefined>;
export type TranslateFn = (key: string, vars?: TranslateVars) => string;

type Messages = Record<string, unknown>;

const catalogs: Record<Locale, Messages> = {
  de: de as Messages,
  en: en as Messages,
};

const STORAGE_KEY = 'vcs-locale';

let activeLocale: Locale = 'en';
let activeTranslate: TranslateFn = (key) => key;

function lookup(source: Messages, path: string): string | undefined {
  const parts = path.split('.');
  let current: unknown = source;
  for (const part of parts) {
    if (current == null || typeof current !== 'object') {
      return undefined;
    }
    current = (current as Record<string, unknown>)[part];
  }
  return typeof current === 'string' ? current : undefined;
}

function interpolate(template: string, vars?: TranslateVars): string {
  if (!vars) {
    return template;
  }
  return template.replace(/\{\{(\w+)\}\}/g, (_, name: string) => {
    const value = vars[name];
    return value == null ? `{{${name}}}` : String(value);
  });
}

function makeTranslate(locale: Locale): TranslateFn {
  return (key, vars) => {
    const raw = lookup(catalogs[locale], key) ?? lookup(catalogs.en, key);
    if (raw == null) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(`[i18n] missing key: ${key}`);
      }
      return key;
    }
    return interpolate(raw, vars);
  };
}

function collectKeys(node: unknown, prefix = '', into: string[] = []): string[] {
  if (typeof node === 'string') {
    if (prefix) {
      into.push(prefix);
    }
    return into;
  }
  if (node && typeof node === 'object' && !Array.isArray(node)) {
    for (const [key, value] of Object.entries(node as Record<string, unknown>)) {
      collectKeys(value, prefix ? `${prefix}.${key}` : key, into);
    }
  }
  return into;
}

export function catalogKeys(locale: Locale = 'en'): string[] {
  return collectKeys(catalogs[locale]).sort();
}

export function hasKey(key: string, locale: Locale = activeLocale): boolean {
  return lookup(catalogs[locale], key) != null || lookup(catalogs.en, key) != null;
}

export function detectLocale(): Locale {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'de' || stored === 'en') {
      return stored;
    }
  } catch {
    // ignore private mode
  }
  const language = (typeof navigator !== 'undefined' ? navigator.language : 'en').toLowerCase();
  return language.startsWith('de') ? 'de' : 'en';
}

export function t(key: string, vars?: TranslateVars): string {
  return activeTranslate(key, vars);
}

export function currentLocale(): Locale {
  return activeLocale;
}

export function bcp47(locale: Locale = activeLocale): string {
  return locale === 'de' ? 'de-DE' : 'en-US';
}

export function formatDateTime(value?: Date | number | string | null): string {
  if (value == null || value === '') {
    return t('common.na');
  }
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return t('common.na');
  }
  return date.toLocaleString(bcp47());
}

export function formatTime(value: Date | number | string): string {
  const date = value instanceof Date ? value : new Date(value);
  return date.toLocaleTimeString(bcp47());
}

export function formatRelativeTime(timestamp?: number, longForm = false): string {
  if (!timestamp) {
    return t('time.never');
  }
  const minutes = Math.floor((Date.now() - timestamp) / 60000);
  if (minutes < 1) {
    return t('time.justNow');
  }
  if (minutes < 60) {
    return t(longForm ? 'time.minutesAgoLong' : 'time.minutesAgo', { count: minutes });
  }
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    return t(longForm ? 'time.hoursAgoLong' : 'time.hoursAgo', { count: hours });
  }
  const days = Math.floor(hours / 24);
  return t(longForm ? 'time.daysAgoLong' : 'time.daysAgo', { count: days });
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes)) {
    return t('common.na');
  }
  if (bytes < 1024) {
    return t('bytes.b', { count: bytes });
  }
  if (bytes < 1024 * 1024) {
    return t('bytes.kb', { count: (bytes / 1024).toFixed(1) });
  }
  return t('bytes.mb', { count: (bytes / (1024 * 1024)).toFixed(1) });
}

function applyLocale(next: Locale) {
  activeLocale = next;
  activeTranslate = makeTranslate(next);
  if (typeof document !== 'undefined') {
    document.documentElement.lang = next;
    document.title = t('app.documentTitle');
  }
}

activeTranslate = makeTranslate(activeLocale);

type I18nContextValue = {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: TranslateFn;
};

const I18nContext = createContext<I18nContextValue | null>(null);

export const I18nProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>(() => {
    const detected = detectLocale();
    applyLocale(detected);
    return detected;
  });

  useEffect(() => {
    applyLocale(locale);
    try {
      localStorage.setItem(STORAGE_KEY, locale);
    } catch {
      // ignore quota / private mode
    }
  }, [locale]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
  }, []);

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t: makeTranslate(locale),
    }),
    [locale, setLocale],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
};

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) {
    throw new Error('useI18n requires I18nProvider');
  }
  return context;
}

export function schemaGroupTitle(groupId: string): string {
  return t(`schema.groups.${groupId}.title`);
}

export function schemaFieldTitle(groupId: string, fieldKey: string, fallback?: string): string {
  const key = `schema.groups.${groupId}.fields.${fieldKey}.title`;
  return hasKey(key) ? t(key) : (fallback || fieldKey);
}

export function schemaFieldDescription(groupId: string, fieldKey: string): string | undefined {
  const key = `schema.groups.${groupId}.fields.${fieldKey}.description`;
  return hasKey(key) ? t(key) : undefined;
}

export function schemaOptionLabel(
  groupId: string,
  fieldKey: string,
  value: string | number | boolean,
  fallback?: string,
): string {
  const key = `schema.groups.${groupId}.fields.${fieldKey}.options.${String(value)}`;
  return hasKey(key) ? t(key) : (fallback || String(value));
}
